import { useParams, Link, Navigate } from "react-router-dom";
import { Check, Phone, MessageCircle, ArrowRight, ArrowLeft } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { CtaBand } from "@/components/CtaBand";
import { getService, SERVICES } from "@/data/services";
import { container, sectionPad, btnPrimary, btnOutlineNavy, cardShadow } from "@/lib/ui";
import { BUSINESS, telLink, whatsappLink } from "@/data/business";
import { cleaningServiceSchema, breadcrumbSchema } from "@/lib/schema";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getService(slug);
  if (!service) return <Navigate to="/services" replace />;

  const Icon = service.icon;
  const related = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);
  const quoteLink = `/quote?service=${encodeURIComponent(service.title)}`;

  return (
    <>
      <Seo
        title={`${service.title} Melbourne`}
        description={`${service.short} Professional ${service.title.toLowerCase()} in Melbourne by JK House Cleaning. ABN registered, insured & police checked. Free quote.`}
        path={`/services/${slug}`}
        schema={[
          cleaningServiceSchema(service),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
            { name: service.title, path: `/services/${slug}` },
          ]),
        ]}
      />
      <PageHeader
        eyebrow="Service"
        title={service.title}
        subtitle={service.short}
        crumbs={[{ name: "Services", path: "/services" }, { name: service.title }]}
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <Link to={quoteLink} className={btnPrimary} data-testid="service-detail-quote">
            Get a Free Quote <ArrowRight className="w-4 h-4" />
          </Link>
          <a href={telLink} className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/70 px-7 py-3.5 font-semibold text-white hover:bg-white hover:text-navy transition-colors">
            <Phone className="w-4 h-4" /> Call {BUSINESS.phoneDisplay}
          </a>
        </div>
      </PageHeader>

      <section className={`${sectionPad} bg-white`}>
        <div className={`${container} grid lg:grid-cols-[1.2fr_0.8fr] gap-12 items-start`}>
          <div>
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img src={service.image} alt={`${service.title} in Melbourne`} className="w-full h-80 object-cover" loading="lazy" />
            </div>
            <div className="mt-8 flex items-center gap-3">
              <span className="grid place-items-center w-12 h-12 rounded-xl bg-navy">
                <Icon className="w-6 h-6 text-brandgreen-light" />
              </span>
              <h2 className="font-heading text-2xl font-bold text-navy">About this service</h2>
            </div>
            <p className="mt-4 text-gray-600 leading-relaxed text-lg">{service.long}</p>

            <h3 className="mt-10 font-heading text-xl font-semibold text-navy">What's typically included</h3>
            <ul className="mt-5 grid sm:grid-cols-2 gap-3">
              {service.includes.map((item) => (
                <li key={item} className="flex items-start gap-3 rounded-xl bg-neutralbg/70 px-4 py-3">
                  <Check className="w-5 h-5 text-brandgreen shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-sm text-gray-500">
              Every clean is tailored to your home — tell us what matters most and we'll build your quote around it.
            </p>
          </div>

          {/* Sticky quote card */}
          <aside className={`lg:sticky lg:top-24 rounded-2xl border border-gray-100 bg-white p-7 ${cardShadow}`}>
            <h3 className="font-heading text-xl font-bold text-navy">Request a free quote</h3>
            <p className="mt-2 text-sm text-gray-600">
              No obligation. We usually reply the same business day.
            </p>
            <div className="mt-6 flex flex-col gap-3">
              <Link to={quoteLink} className={btnPrimary} data-testid="service-aside-quote">
                Get a Free Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={telLink} className={btnOutlineNavy}>
                <Phone className="w-4 h-4" /> Call {BUSINESS.phoneDisplay}
              </a>
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-brandgreen/10 px-7 py-3.5 font-semibold text-brandgreen-dark hover:bg-brandgreen hover:text-white transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp Us
              </a>
            </div>
            <div className="mt-6 pt-6 border-t border-gray-100 text-xs text-gray-500 space-y-1.5">
              <p>✓ ABN Registered &amp; Insured</p>
              <p>✓ Police Checked</p>
              <p>✓ Equipment &amp; products supplied</p>
            </div>
          </aside>
        </div>
      </section>

      <section className="pb-20 bg-white">
        <div className={container}>
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-2xl font-bold text-navy">Related services</h2>
            <Link to="/services" className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-brandgreen">
              <ArrowLeft className="w-4 h-4" /> All services
            </Link>
          </div>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((s, i) => {
              const RIcon = s.icon;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className={`group flex items-center gap-4 rounded-2xl bg-neutralbg/70 border border-gray-100 p-5 transition-transform hover:-translate-y-1 hover:bg-white ${cardShadow}`}
                >
                  <span className="grid place-items-center w-12 h-12 rounded-xl bg-white shadow-sm">
                    <RIcon className="w-6 h-6 text-navy" />
                  </span>
                  <span>
                    <span className="block font-heading font-semibold text-navy group-hover:text-brandgreen transition-colors">{s.title}</span>
                    <span className="block text-xs text-gray-500 mt-0.5">Learn more →</span>
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
